/**
 * Production-grade API service layer
 * Handles all HTTP requests with interceptors, error handling, and retry logic
 */

import axios from 'axios';
import { API_CONFIG } from '../config/apiConfig';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token to requests (if needed in future)
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token from localStorage if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log the full URL being requested for debugging
    const fullUrl = `${config.baseURL}${config.url}`;
    console.log('🚀 API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: fullUrl,
      headers: config.headers,
    });
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', {
      status: response.status,
      url: response.config.url,
      fullURL: `${response.config.baseURL}${response.config.url}`,
      data: response.data,
    });
    return response;
  },
  (error) => {
    // Ignore canceled/aborted requests (expected in React StrictMode or when requests are cancelled)
    if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || error.message === 'canceled') {
      // Don't log or handle canceled errors - they're expected
      return Promise.reject(error);
    }
    
    // Handle common error cases
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          localStorage.removeItem('auth_token');
          if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
            window.location.href = '/login';
          }
          break;
        case 403:
          console.error('Access forbidden:', data?.message || 'Forbidden');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_profile');
          if (window.location.pathname !== '/login' && window.location.pathname !== '/signup') {
            window.location.href = '/login';
          }
          break;
        case 404:
          console.error('Resource not found (404):', data?.message || 'Not Found');
          console.error('Request URL:', `${error.config?.baseURL}${error.config?.url}`);
          console.error('Response data:', data);
          // Check if the error message indicates client ID issue
          if (data?.message && data.message.toLowerCase().includes('org')) {
            console.error('⚠️ Org ID issue detected. Verify org identifier matches backend configuration.');
          }
          break;
        case 500:
          console.error('Server error:', data?.message || 'Internal Server Error');
          break;
        case 504:
          console.error('Gateway timeout - server took too long to respond');
          break;
        default:
          console.error('API Error:', data?.message || 'An error occurred');
      }
    } else if (error.request) {
      // Request made but no response received (CORS, network, timeout, or server not responding)
      console.error('Network error: No response received from server');
      console.error('Request details:', {
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method,
        fullURL: `${error.config?.baseURL}${error.config?.url}`,
        timeout: error.config?.timeout,
      });
      
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        console.error('⚠️ Request timed out - the server may be taking too long to respond');
      }
    } else {
      // Something else happened
      console.error('Error:', error.message);
      console.error('Error details:', error);
    }

    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // GET request
  get: async (url, config = {}) => {
    try {
      // Ensure URL starts with / to avoid issues
      const cleanUrl = url.startsWith('/') ? url : `/${url}`;
      const response = await apiClient.get(cleanUrl, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // POST request
  post: async (url, data, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PUT request
  put: async (url, data, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PATCH request
  patch: async (url, data, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default apiClient;

